import React, { FC, useState, useEffect, useRef } from 'react';
import styles from './MultiSelect.module.css';

export enum MultiSelectType {
    variant1 = 'custom-select-variant-1',
    variant2 = 'custom-select-variant-2',
}

export type SimpleOptionsList = { value: string; text: string }[];

export interface MultiSelectProps {
    id?: string;
    classes?: string;
    name?: string;
    type: MultiSelectType;
    disabled?: boolean;
    readonly?: boolean;
    options: SimpleOptionsList;
    required?: boolean;
    width?: string;
    value?: string[];
    onChange?: (value: string[]) => void;
    placeholder?: string;
}

export const MultiSelect: FC<MultiSelectProps> = ({
    id,
    classes,
    name,
    type,
    disabled,
    readonly,
    options,
    required,
    width,
    value = [],
    onChange,
    placeholder = 'Selecione...',
}) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(value);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedValues(value);
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggleOption = (optionValue: string) => {
        if (disabled || readonly) return;

        let newValues: string[];
        if (selectedValues.includes(optionValue)) {
            newValues = selectedValues.filter((v) => v !== optionValue);
        } else {
            newValues = [...selectedValues, optionValue];
        }
        setSelectedValues(newValues);
        onChange && onChange(newValues);
    };

    const handleSelectAll = () => {
        if (disabled || readonly) return;
        const allValues = options.map((opt) => opt.value);
        setSelectedValues(allValues);
        onChange && onChange(allValues);
    };

    const handleClear = () => {
        if (disabled || readonly) return;
        setSelectedValues([]);
        onChange && onChange([]);
    };

    const getDisplayText = () => {
        if (selectedValues.length === 0) {
            return placeholder;
        }
        const selectedTexts = options
            .filter((opt) => selectedValues.includes(opt.value))
            .map((opt) => opt.text);
        return selectedTexts.join(', ');
    };

    return (
        <div
            ref={containerRef}
            className={`${styles[type]} ${classes || ''} ${styles['multi-select-container']}`}
            style={width ? { width: width } : undefined}
        >
            <div
                id={id || undefined}
                className={`${styles['multi-select-trigger']} ${disabled ? styles['disabled'] : ''}`}
                onClick={() => !disabled && !readonly && setIsOpen(!isOpen)}
                aria-disabled={disabled}
                aria-readonly={readonly}
            >
                <span className={styles['selected-text']}>{getDisplayText()}</span>
                <i className="fa-light fa-chevron-down" />
            </div>
            {isOpen && (
                <div className={styles['multi-select-dropdown']}>
                    <div className={styles['dropdown-header']}>
                        <button
                            type="button"
                            className={styles['dropdown-header-action']}
                            onClick={handleSelectAll}
                        >
                            Selecionar todos
                        </button>
                        <button
                            type="button"
                            className={styles['dropdown-header-action']}
                            onClick={handleClear}
                        >
                            Limpar
                        </button>
                    </div>
                    <div className={styles['dropdown-options']}>
                        {options.map((item, index) => {
                            const isChecked = selectedValues.includes(item.value);
                            return (
                                <label
                                    key={`option-${index}`}
                                    className={styles['multi-select-option']}
                                >
                                    <input
                                        type="checkbox"
                                        name={name}
                                        value={item.value}
                                        checked={isChecked}
                                        onChange={() => handleToggleOption(item.value)}
                                        required={required && selectedValues.length === 0}
                                    />
                                    <span>{item.text}</span>
                                    <div className={`${styles['checkbox-custom']} ${isChecked ? styles['checked'] : ''}`}>
                                        {isChecked && <i className="fa-solid fa-check" />}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
